import styled, { css } from "styled-components";

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.cardBg ?? theme.colors.white};
  border-radius: 8px;
  box-shadow: 0 1px 3px ${({ theme }) => theme.colors.cardShadow};
`;

export const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 24px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;

  .ant-typography.paic-page-title {
    margin: 0;
  }
`;

export const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 12px;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

export const BackButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.white};
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.dark};
  flex-shrink: 0;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const GroupNameBadge = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 400;
`;

export const TabsAndControlsBlock = styled.div`
  display: flex;
  flex-direction: column;
`;



export const TabLabelWithCaret = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .anticon {
    font-size: 10px;
  }
`;

export const ContentSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(300px, 22vw, 360px);
  gap: 16px;
  flex: 1;
  min-height: 0;
  padding: 16px 24px 24px;
  overflow: visible;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 1400px) and (min-height: 850px) {
    align-items: stretch;
  }
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  gap: 10px;
  overflow: hidden;
`;

export const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  min-height: 0;
  overflow: visible;
  align-self: start;
  padding-bottom: 16px;
`;

export const DetailCardShell = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;

  .paic-detail-card {
    width: 100%;
    flex: 1;
    border: 1px solid ${({ theme }) => theme.colors.borderLight} !important;
    border-radius: 0 !important;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.white} !important;
    box-shadow: none !important;
    display: flex;
    flex-direction: column;
  }

  .paic-detail-card .ant-card-body {
    padding: 16px !important;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    min-height: 0;
  }

  .ant-typography.paic-section-title {
    margin: 0 0 2px;
  }

  .ant-typography.paic-meta-value {
    color: ${({ theme }) => theme.colors.dark};
    font-size: 14px;
    line-height: 1.4;
  }

  .ant-typography.paic-meta-paragraph {
    margin: 0;
    color: ${({ theme }) => theme.colors.dark};
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-wrap;
  }
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

export const AgentCodeBadge = styled.div`
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.dark};
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
`;

export const MetaStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
`;

export const MetaRow = styled.div`
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr) auto;
  gap: 14px;
  align-items: ${({ $alignTop }) => ($alignTop ? "start" : "center")};
  min-width: 0;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

export const MetaLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray};
  padding-top: 2px;
`;

export const MetaValueWrap = styled.div`
  min-width: 0;
  word-break: break-word;
`;

export const EditIconButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.gray};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
  margin-top: 2px;

  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.hoverBg};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const EditFieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-input-textarea textarea {
    border-radius: 6px;
  }
`;

export const ActionButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  background: ${({ theme }) => theme.colors.white};
  overflow: hidden;
`;

export const TableShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  min-width: 0;
  width: 100%;
`;

export const TableScrollWrapper = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: auto;
  max-height: calc(100vh - 430px);

  @media (min-height: 1200px) {
    max-height: calc(100vh - 390px);
  }
  @media (max-height: 900px) {
    max-height: calc(100vh - 400px);
  }
  @media (max-height: 800px) {
    max-height: calc(100vh - 360px);
  }
  @media (max-width: 1200px) {
    max-height: none;
  }

  .ant-table-wrapper,
  .ant-table {
    width: 100%;
    min-width: 0;
  }

  .ant-table {
    width: 100% !important;
  }

  .ant-table-container {
    border: none;
    background: ${({ theme }) => theme.colors.white};
    width: 100%;
    min-width: 0;
  }

  .ant-table-content {
    overflow-x: auto !important;
  }

  .ant-table-thead {
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .ant-table-thead > tr > th {
    padding: 12px 16px !important;
    background: ${({ theme }) => theme.colors.tableHeaderBg} !important;
    border-bottom: 2px solid ${({ theme }) => theme.colors.borderLight} !important;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.gray} !important;
  }

  .ant-table-tbody > tr > td {
    padding: 14px 16px !important;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight} !important;
  }

  .agent-details-table .ant-table-tbody > tr.ant-table-row {
    cursor: pointer;
    transition: background 0.18s ease;
  }

  .agent-details-table .ant-table-tbody > tr.ant-table-row:hover > td {
    background: ${({ theme }) => theme.colors.tableRowHoverBg} !important;
  }

  .agent-details-table .ant-table-expanded-row > td {
    background: ${({ theme }) => theme.colors.hoverBg} !important;
    padding: 0 !important;
  }

  .ant-table-container::before,
  .ant-table-container::after {
    display: none !important;
  }
`;

export const ExpandedContent = styled.div`
  padding: 18px 18px 18px 28px;
  background: ${({ theme }) => theme.colors.hoverBg};
`;

export const ExpandedContentHeader = styled.div`
  
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 12px;
`;

export const MicrotaskList = styled.ol`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const MicrotaskItem = styled.li`
  color: ${({ theme }) => theme.colors.dark};
  font-size: 14px;
  line-height: 1.6;
`;

export const EmptyMicrotasks = styled.div`
  color: ${({ theme }) => theme.colors.gray};
  font-size: 14px;
  line-height: 1.6;
`;

export const PotentialValue = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.successBg};
  color: ${({ theme }) => theme.colors.successText};
  font-size: 14px;
  font-weight: 600;
`;

export const ExpandIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray};
  transition: transform 0.18s ease;

  ${({ $expanded }) =>
    $expanded &&
    css`
      transform: rotate(180deg);
      color: ${({ theme }) => theme.colors.primary};
    `}
`;

export const StatsCardContainer = styled.div`
  width: 100%;
  min-width: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  max-height: clamp(500px, 52vh, 580px);

  .paic-stats-card { 
    width: 100%; 
    height: 100%;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 12px ${({ theme }) => theme.colors.cardShadow} !important;
  }

  .paic-stats-card .ant-card-head { 
    min-height: auto !important; 
    padding: 0 !important; 
    background: ${({ theme }) => theme.colors.primary}; 
    flex-shrink: 0;
  }

  .paic-stats-card .ant-card-head-title { 
    padding: 0 !important; 
    white-space: normal !important; 
  }

  .paic-stats-card .ant-card-body {
    padding: clamp(12px, 1vw, 16px) !important;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-height: 0;
    overflow-y: auto !important;
  }
`;

export const StatsCardHeader = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const StatsCardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.white};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
`;

export const StatsCardSubtitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.25;
`;

export const StatsCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const StatsPrimaryRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  min-width: 0;
`;

export const StatsBigValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark};
  line-height: 1.2;
  margin-bottom: 3px;
  word-break: break-word;
`;

export const StatsLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray};
`;

export const StatsRoiBadge = styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 7px;
  padding: 0.4rem 0.8rem;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.dark};
  white-space: nowrap;
  align-self: flex-start;
  margin-top: 2px;
`;

export const StatsTileGrid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 9px;
`;

export const StatsTileGrid2 = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
`;

export const StatsTile = styled.div`
  background: ${({ theme }) => theme.colors.light};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 8px;
  padding: 8px 6px;
  text-align: center;
  min-width: 0;
`;

export const StatsTileLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  line-height: 1.2;
`;

export const StatsTileValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark};
  word-break: break-word;
`;

export const ComingSoonBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;